import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { Product } from "../../ProductMFE/Products/types/products";
import type { GetProductListRequest } from "../../ProductMFE/Products/types/products";
import type { FilterFormState } from "../../ProductMFE/Products/filters/types";
import { initialProductListFilters } from "../../ProductMFE/Products/filters/types";

export interface ProductState {
  productList: Product[];
  productDetail?: Product;
  productListFilterForm: FilterFormState;
  productListRequest: GetProductListRequest;
  productListRefreshTrigger: number;
  productListTotalElements: number;
  productListTotalPages: number;
  productListCurrentPage: number;
  productListPageSize: number;
  lowStockAlerts: Product[];
  /** COMPANY_ADMIN: optional catalog store filter for product list only (not used for create product). */
  companyAdminProductListStoreIdByCompany: Record<string, string>;
}

export const initialProductState: ProductState = {
  productList: [],
  productDetail: undefined,
  productListFilterForm: initialProductListFilters,
  productListRequest: {},
  productListRefreshTrigger: 0,
  productListTotalElements: 0,
  productListTotalPages: 0,
  productListCurrentPage: 0,
  productListPageSize: 20,
  lowStockAlerts: [],
  companyAdminProductListStoreIdByCompany: {}
};

export const productSlice = createSlice({
  name: "product",
  initialState: initialProductState,
  reducers: {
    setProductList: (state, action: PayloadAction<Product[]>) => {
      state.productList = action.payload;
    },
    setProductListPagination: (
      state,
      action: PayloadAction<{
        totalElements: number;
        totalPages: number;
        currentPage: number;
        pageSize: number;
      }>
    ) => {
      state.productListTotalElements = action.payload.totalElements;
      state.productListTotalPages = action.payload.totalPages;
      state.productListCurrentPage = action.payload.currentPage;
      state.productListPageSize = action.payload.pageSize;
    },
    resetProductList: (state) => {
      state.productList = initialProductState.productList;
    },
    addProductList: (state, action: PayloadAction<Product[]>) => {
      state.productList = [...state.productList, ...action.payload];
    },
    setProductDetail: (state, action: PayloadAction<Product>) => {
      state.productDetail = action.payload;
    },
    resetProductDetail: (state) => {
      state.productDetail = initialProductState.productDetail;
    },
    setProductListFilterForm: (
      state,
      action: PayloadAction<FilterFormState>
    ) => {
      state.productListFilterForm = action.payload;
    },
    updateProductListFilterForm: (
      state,
      action: PayloadAction<{
        key: keyof FilterFormState;
        value: FilterFormState[keyof FilterFormState];
      }>
    ) => {
      const { key, value } = action.payload;
      (state.productListFilterForm as FilterFormState)[key] = value as never;
    },
    setProductListRequest: (
      state,
      action: PayloadAction<GetProductListRequest>
    ) => {
      state.productListRequest = action.payload;
    },
    resetProductListFilters: (state) => {
      state.productListFilterForm = initialProductListFilters;
      state.productListRequest = { page: 0, size: 20 };
      state.productListTotalElements = 0;
      state.productListTotalPages = 0;
      state.productListCurrentPage = 0;
      state.productListPageSize = 20;
    },
    incrementProductListRefreshTrigger: (state) => {
      state.productListRefreshTrigger += 1;
    },
    setCompanyAdminProductListStoreFilter: (
      state,
      action: PayloadAction<{ companyId: string; storeId: string | null }>
    ) => {
      const companyId = action.payload.companyId?.trim();
      if (!companyId) return;
      const sid = action.payload.storeId?.trim();
      if (!sid) {
        delete state.companyAdminProductListStoreIdByCompany[companyId];
      } else {
        state.companyAdminProductListStoreIdByCompany[companyId] = sid;
      }
    },
    setLowStockAlerts: (state, action: PayloadAction<Product[]>) => {
      state.lowStockAlerts = action.payload;
    }
  }
});

export const {
  setProductList,
  setProductListPagination,
  resetProductList,
  addProductList,
  setProductDetail,
  resetProductDetail,
  setProductListFilterForm,
  updateProductListFilterForm,
  setProductListRequest,
  resetProductListFilters,
  incrementProductListRefreshTrigger,
  setCompanyAdminProductListStoreFilter,
  setLowStockAlerts
} = productSlice.actions;

export default productSlice.reducer;
